import { GridCellParams, GridRenderCellParams } from '@mui/x-data-grid'
import { TypoGraph, Tooltip, Container } from 'components'
import { formatRiskCell } from 'tools/helper'
import { numberOrStringComparator, numberComparator, stringDateComparator } from 'tools/comparators'
import HeaderWithIcon from 'components/tableHeaderWithIcon'
import CircleIcon from '@mui/icons-material/Circle'
import strings from 'strings'
import { getCompanyLabel } from 'tools/companyLabel'

const t = strings.TABLE_DEFAULTS
const COMPANY_LABEL = getCompanyLabel()

const riskScoresColumnFlex = 0.15

const ColumnDescriptionWithoutTitle = (props: any) => {
  const { description, cell } = props
  return (
    <Container
      className="columnDescriptionContainer withoutTitle"
      sx={
        cell && {
          gridTemplateRows: '0.5rem 0.5rem'
        }
      }
    >
      <TypoGraph
        variant="subtitle1"
        content={description}
        className="descriptionText"
        sx={{color:'#C4C4C4', paddingBottom: '1rem!important', marginLeft: 'auto!important', marginRight: 'auto!important'}}
      />
    </Container>
  )
}

const nbAssetActivityList = {
  column: [
    {
      field: 'assetId',
      headerName: 'Asset ID',
      width: 90,
    },
    {
      field: 'assetEldId',
      headerName: 'Asset ID',
      minWidth: 70,
      sortable: true,
      flex: 0.3,
      sortComparator: numberOrStringComparator,
      renderHeader: () => {
        return (
          <ColumnDescriptionWithoutTitle title="Asset" description="ID" />
        )
      },
      renderCell: function (params: GridRenderCellParams) {
        return (
          <Tooltip
            title={params.value}
            containerClassName="ellipsis"
            maxWidth={210}
          >
            <TypoGraph
              component="span"
              variant="body1"
            >
              {params.value}
            </TypoGraph>
          </Tooltip>
        )
      },
    },
    {
      field: 'vin',
      headerName: 'VIN',
      minWidth: 90,
      sortable: true,
      flex: 0.4,
      renderHeader: () => {
        return (
          <ColumnDescriptionWithoutTitle
            title="VIN"
            description="VEHICLE IDENTIFIER"
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return row?.identificationNo || t.DASH_VALUE
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip
            title={params.row?.identificationNo || t.DASH_VALUE}
            containerClassName="ellipsis"
            maxWidth={210}
          >
            <TypoGraph component="span" variant="body1">
              {params.row?.identificationNo || t.DASH_VALUE}
            </TypoGraph>
          </Tooltip>
        )
      }
    },
    {
      field: 'totalMiles',
      headerName: 'Kilometers',
      minWidth: 80,
      flex: 0.2,
      sortable: true,
      cellClassName: 'centeredCell',
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle description="TOTAL KMs" />
      ),
      renderCell: (params: GridRenderCellParams) => {
        return <TypoGraph content={formatRiskCell(params.row?.totalMiles)} />
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row.totalMiles)
      },
    },
    {
      field: 'executionDateFormatted',
      headerName: 'Last Score Date',
      minWidth: 105,
      flex: 0.2,
      cellClassName: 'centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        if (!params.row?.executionDateFormatted) return t.DASH_VALUE
        return (
          <TypoGraph>
            {params.row?.executionDateFormatted}
          </TypoGraph>
        )
      },
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle description="LATEST" />
      ),
      valueGetter: (value: any, row: any) => {
        if (!row?.executionDateFormatted) return t.DASH_VALUE
        return row?.executionDateFormatted
      },
      sortComparator: stringDateComparator,
      sortable: true,
    },
    {
      field: 'assetScore',
      headerName: `${COMPANY_LABEL} Score`,
      headerClassName: 'nbSummaryBorder',
      width: 100,
      flex: 0.2,
      headerAlign: 'center' as const,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle description="SUMMARY" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'summaryScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => (
        <TypoGraph
          sx={{ fontWeight: 'bold' }}
          variant="h6"
          content={formatRiskCell(params.row?.score)}
        />
      ),
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row.score)
      },
    },
    {
      field: 'safetyIndex',
      headerName: 'Safety Index',
      headerClassName: 'nbSafetyBorder',
      width: 80,
      flex: riskScoresColumnFlex,
      headerAlign: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Safety" description="INDEX" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'safetyScoreStart centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return <TypoGraph content={formatRiskCell(params.row?.harsh.index)} />
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.harsh.index)
      },
    },
    {
      field: 'safetySev1',
      headerName: 'Safety High-G',
      width: 80,
      sortable: true,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Safety" description="HIGH-G" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'safetyScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.harsh?.severity1, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.harsh?.severity1, 1)
      },
    },
    {
      field: 'safetySev2',
      headerName: 'Safety Brake',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Safety" description="BRAKE" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'safetyScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.harsh?.severity2, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.harsh?.severity2, 1)
      },
    },
    {
      field: 'safetySev3',
      headerName: 'Safety Cor/Acc',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Safety" description="COR/ACC" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'safetyScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.harsh?.severity3, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.harsh?.severity3, 1)
      },
    },
    {
      field: 'speedingIndex',
      headerName: 'Speeding Index',
      headerClassName: 'nbSpeedingBorder',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Speeding" description="INDEX" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'speedingScoreStart centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return <TypoGraph content={formatRiskCell(params.row?.speed?.index)} />
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.speed?.index)
      },
    },
    {
      field: 'speedingLimitPlus5',
      headerName: 'Speeding Limit Plus 5',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Speeding" description="5-10" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'speedingScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.speed?.pingsOverSpeedLimitPlus5, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.speed?.pingsOverSpeedLimitPlus5, 1)
      },
    },
    {
      field: 'speedingLimitPlus10',
      headerName: 'Speeding Limit Plus 10',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Speeding" description="10.1-15" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'speedingScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.speed?.pingsOverSpeedLimitPlus10, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.speed?.pingsOverSpeedLimitPlus10, 1)
      },
    },
    {
      field: 'speedingLimitPlus15',
      headerName: 'Speeding Limit Plus 15',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Speeding" description="15.1+" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'speedingScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.speed?.pingsOverSpeedLimitPlus15, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.speed?.pingsOverSpeedLimitPlus15, 1)
      },
    },
    {
      field: 'speedingOver75',
      headerName: 'Speeding Over 75',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Speeding" description="75+" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'speedingScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph
            content={formatRiskCell(params.row?.speed?.pingsOver75mph, 1)}
          />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.speed?.pingsOver75mph, 1)
      },
    },
    {
      field: 'geoIndex',
      headerName: 'Geo Index',
      headerClassName: 'nbGeoBorder',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Geo Risk" description="INDEX" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'geoScoreStart centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph content={formatRiskCell(params.row?.geographical?.index)} />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.geographical?.index)
      },
    },
    {
      field: 'geoLow',
      headerName: 'Geo Low',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Geo Risk" description="LOW" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'geoScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph content={formatRiskCell(params.row?.geographical?.low, 1)} />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.geographical?.low, 1)
      },
    },
    {
      field: 'geoMedium',
      headerName: 'Geo Medium',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Geo Risk" description="MED" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'geoScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph content={formatRiskCell(params.row?.geographical?.medium, 1)} />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.geographical?.medium, 1)
      },
    },
    {
      field: 'geoHigh',
      headerName: 'Geo High',
      width: 80,
      flex: riskScoresColumnFlex,
      align: 'center' as const,
      sortable: true,
      sortComparator: numberComparator,
      renderHeader: () => (
        <ColumnDescriptionWithoutTitle title="Geo Risk" description="HIGH" />
      ),
      cellClassName: (params: GridCellParams<any>) =>
        'geoScoreCell centeredCell',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <TypoGraph content={formatRiskCell(params.row?.geographical?.high, 1)} />
        )
      },
      valueGetter: (value: any, row: any) => {
        return formatRiskCell(row?.geographical?.high, 1)
      },
    },
  ],
  groups: [
    {
      groupId: 'asset',
      headerName: 'Asset',
      headerClassName: 'columnGroupTitle',
      renderHeaderGroup: (params: any) => <HeaderWithIcon {...params} />,
      children: [{ field: 'assetEldId' }],
    },
    {
      groupId: 'vin',
      headerName: 'VIN',
      headerClassName: 'columnGroupTitle',
      renderHeaderGroup: (params: any) => <HeaderWithIcon {...params} />,
      children: [{ field: 'vin' }],
    },
    {
      groupId: 'executionDateFormatted',
      headerName: 'Score Date',
      headerClassName: 'columnGroupTitle',
      renderHeaderGroup: (params: any) => <HeaderWithIcon {...params} />,
      children: [{ field: 'executionDateFormatted' }],
    },
    {
      groupId: 'totalMiles',
      headerName: 'Kilometers',
      headerClassName: 'columnGroupTitle',
      renderHeaderGroup: (params: any) => <HeaderWithIcon {...params} />,
      children: [{ field: 'totalMiles' }],
    },
    {
      groupId: 'summaryScore',
      headerName: `${COMPANY_LABEL} Score`,
      headerClassName: 'columnGroupTitle nbSummaryBorder centered',
      renderHeaderGroup: (params: any) => (
        <HeaderWithIcon
          {...params}
          data-testid="nb-table-score-group-header"
          icon={
            <CircleIcon
              fontSize="small"
              sx={{
                color: '#7B1FA2',
                width: '1rem',
                height: 'auto',
                paddingRight: '0.2rem',
              }}
            />
          }
        />
      ),
      children: [{ field: 'assetScore' }],
    },
    {
      groupId: 'safety',
      headerName: 'Safety Events',
      description: 'per 1k KMs',
      headerClassName: 'columnGroupTitle nbSafetyBorder centered',
      renderHeaderGroup: (params: any) => (
        <HeaderWithIcon
          {...params}
          icon={
            <CircleIcon
              fontSize="small"
              sx={{
                color: '#212121',
                width: '1rem',
                height: 'auto',
                paddingRight: '0.2rem',
              }}
            />
          }
        />
      ),
      children: [
        { field: 'safetyIndex' },
        { field: 'safetySev1' },
        { field: 'safetySev2' },
        { field: 'safetySev3' },
      ],
    },
    {
      groupId: 'speeding',
      headerName: 'Speeding %',
      description: 'time over limit',
      headerClassName: 'columnGroupTitle nbSpeedingBorder centered',
      renderHeaderGroup: (params: any) => (
        <HeaderWithIcon
          {...params}
          icon={
            <CircleIcon
              fontSize="small"
              sx={{
                color: '#9E9E9E',
                width: '1rem',
                height: 'auto',
                paddingRight: '0.2rem',
              }}
            />
          }
        />
      ),
      children: [
        { field: 'speedingIndex' },
        { field: 'speedingLimitPlus5' },
        { field: 'speedingLimitPlus10' },
        { field: 'speedingLimitPlus15' },
        { field: 'speedingOver75' },
      ],
    },
    {
      groupId: 'geographic',
      headerName: 'Geo Risk',
      description: 'KMs in thousands',
      headerClassName: 'columnGroupTitle nbGeoBorder centered',
      renderHeaderGroup: (params: any) => (
        <HeaderWithIcon
          {...params}
          icon={
            <CircleIcon
              fontSize="small"
              sx={{
                color: '#795548',
                width: '1rem',
                height: 'auto',
                paddingRight: '0.2rem',
              }}
            />
          }
        />
      ),
      children: [
        { field: 'geoIndex' },
        { field: 'geoLow' },
        { field: 'geoMedium' },
        { field: 'geoHigh' },
      ],
    },
  ],
}

export { nbAssetActivityList }
